/* Product funnel sections (pp-*) – shared behaviour. Safe to load more than once. */
(() => {
  if (window.__ppFunnel) { window.__ppFunnel.init(document); return; }

  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');
  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  };

  /* ---------- Carousels ---------- */
  function initCarousel(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const track = root.querySelector('.pp-carousel__track');
    const prev = root.querySelector('[data-pp-prev]');
    const next = root.querySelector('[data-pp-next]');
    const dotsWrap = root.querySelector('.pp-carousel__dots');
    if (!track) return;
    const slides = Array.from(track.children);
    const step = () => (slides[0] ? slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : track.clientWidth);
    let dots = [];
    if (dotsWrap) {
      dotsWrap.innerHTML = '';
      dots = slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pp-carousel__dot';
        b.setAttribute('aria-label', 'Slide ' + (i + 1));
        b.addEventListener('click', () => track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' }));
        dotsWrap.appendChild(b);
        return b;
      });
    }
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      // Hide the whole nav when every slide already fits (e.g. 3 cards on desktop).
      root.classList.toggle('pp-carousel--static', track.scrollWidth - track.clientWidth < 5);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      const idx = Math.round(track.scrollLeft / step());
      dots.forEach((d, i) => d.setAttribute('aria-current', i === idx ? 'true' : 'false'));
    };
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
    window.addEventListener('load', update);
    if (window.ResizeObserver) new ResizeObserver(update).observe(track);

    const auto = parseInt(root.dataset.ppAutoplay || '0', 10);
    if (auto > 0) {
      let timer = setInterval(() => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (track.scrollLeft >= max) track.scrollTo({ left: 0, behavior: 'smooth' });
        else track.scrollBy({ left: step(), behavior: 'smooth' });
      }, auto * 1000);
      root.addEventListener('pointerenter', () => clearInterval(timer));
    }
  }

  /* ---------- Countdown bar ---------- */
  function initCountdown(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const mode = root.dataset.mode;
    const h = root.querySelector('[data-h]'), m = root.querySelector('[data-m]'), s = root.querySelector('[data-s]');
    const key = 'pp-cd-' + root.dataset.id;
    let end;
    if (mode === 'date' && root.dataset.end) {
      end = new Date(root.dataset.end).getTime();
    } else if (mode === 'midnight') {
      const d = new Date(); d.setHours(24, 0, 0, 0); end = d.getTime();
    } else {
      const hours = parseFloat(root.dataset.hours || '12');
      end = parseInt(store.get(key) || '0', 10);
      if (!end || end < Date.now()) { end = Date.now() + hours * 3600000; store.set(key, end); }
    }
    const tick = () => {
      let left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (left === 0 && mode !== 'date') {
        if (mode === 'midnight') { const d = new Date(); d.setHours(24, 0, 0, 0); end = d.getTime(); }
        else { end = Date.now() + parseFloat(root.dataset.hours || '12') * 3600000; store.set(key, end); }
        left = Math.floor((end - Date.now()) / 1000);
      }
      if (left === 0 && mode === 'date' && root.dataset.hideExpired === 'true') { root.hidden = true; return; }
      if (h) h.textContent = pad(Math.floor(left / 3600));
      if (m) m.textContent = pad(Math.floor((left % 3600) / 60));
      if (s) s.textContent = pad(left % 60);
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ---------- Offer expiry (minutes:seconds, per visitor session) ---------- */
  function initExpiry(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const out = root.querySelector('.pp-expiry__time');
    const minutes = parseFloat(root.dataset.minutes || '10');
    const key = 'pp-exp-' + root.dataset.id;
    let end = parseInt(store.get(key) || '0', 10);
    if (!end || end < Date.now()) { end = Date.now() + minutes * 60000; store.set(key, end); }
    const tick = () => {
      let left = Math.floor((end - Date.now()) / 1000);
      if (left <= 0) { end = Date.now() + minutes * 60000; store.set(key, end); left = minutes * 60; }
      out.textContent = pad(Math.floor(left / 60)) + ':' + pad(left % 60);
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ---------- Timeline fill ---------- */
  function initTimeline(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const items = Array.from(root.querySelectorAll('.pp-tl__item, .pp-journey__step'));
    const update = () => {
      const rect = root.getBoundingClientRect();
      const trigger = window.innerHeight * 0.6;
      const pct = Math.min(100, Math.max(0, ((trigger - rect.top) / rect.height) * 100));
      root.style.setProperty('--pp-fill', pct + '%');
      items.forEach((it) => it.classList.toggle('is-active', it.getBoundingClientRect().top < trigger));
    };
    window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    update();
  }

  /* ---------- Mini review slider ---------- */
  function initMiniReviews(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const slides = Array.from(root.querySelectorAll('.pp-mini-review'));
    if (slides.length < 2) return;
    let i = 0;
    const show = (n) => { i = (n + slides.length) % slides.length; slides.forEach((s, k) => s.classList.toggle('is-active', k === i)); };
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-pp-prev]')) show(i - 1);
      if (e.target.closest('[data-pp-next]')) show(i + 1);
    });
  }

  /* ---------- Scroll from the rating line to the review widget ---------- */
  function initScrollLink(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const find = () => {
      const list = (root.dataset.ppScroll || '').split(',').map((s) => s.trim()).filter(Boolean);
      for (const sel of list) {
        let el = null;
        try { el = document.querySelector(sel); } catch (e) { el = null; }
        if (el && el.getBoundingClientRect().height > 0) return el;
      }
      return null;
    };
    root.addEventListener('click', (e) => {
      const target = find();
      if (!target) {
        // Review widget not on the page: never let href="#" jump to the top.
        if ((root.getAttribute('href') || '').trim() === '#') e.preventDefault();
        return;
      }
      e.preventDefault();
      const offset = parseInt(root.dataset.ppScrollOffset || '80', 10);
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }

  /* ---------- Bundle selector -> product form quantity ---------- */
  function initBundles(root) {
    if (root.dataset.ppReady) return;
    root.dataset.ppReady = '1';
    const qty = root.querySelector('[data-pp-qty]');
    const plan = root.querySelector('[data-pp-plan]');
    const planToggle = root.querySelector('[data-pp-sub-toggle]');
    root.addEventListener('change', (e) => {
      if (e.target.matches('input[data-pp-tier]')) qty.value = e.target.value;
      if (planToggle && e.target === planToggle && plan) plan.disabled = !planToggle.checked;
    });
    const checked = root.querySelector('input[data-pp-tier]:checked');
    if (checked) qty.value = checked.value;
  }

  /* ---------- Jump links: any button linking to #buy or #reviews ---------- */
  const PP_JUMPS = {
    buy: '.kaching-bundles, .pp-bundles, .product-info__buy-buttons, .buy-buttons, .pp-boffer__btn, [name="add"]',
    'buy-buttons': '.kaching-bundles, .pp-bundles, .product-info__buy-buttons, .buy-buttons, .pp-boffer__btn, [name="add"]',
    atc: '.kaching-bundles, .pp-bundles, .product-info__buy-buttons, .buy-buttons, .pp-boffer__btn, [name="add"]',
    cart: '.kaching-bundles, .pp-bundles, .product-info__buy-buttons, .buy-buttons, .pp-boffer__btn, [name="add"]',
    reviews: '#looxReviews, .loox-reviews-default, #loox-reviews, [data-loox-reviews]'
  };

  function ppFirstVisible(selectors) {
    for (const sel of selectors.split(',').map((s) => s.trim()).filter(Boolean)) {
      let el = null;
      try { el = document.querySelector(sel); } catch (e) { el = null; }
      if (el && el.getBoundingClientRect().height > 0) return el;
    }
    return null;
  }

  const PP_CTA = '.product-info__buy-buttons, .buy-buttons, .pp-boffer__btn, [name="add"]';

  function ppScrollTo(el, offset) {
    const pageY = window.pageYOffset;
    // The theme's own scroll-padding-top says how much room the sticky header needs
    // (it slides back in on an upward jump). Never stop higher than that.
    const pad = parseInt(getComputedStyle(document.documentElement).scrollPaddingTop, 10);
    const gap = Math.max(offset || 90, isNaN(pad) ? 0 : pad);
    let top = el.getBoundingClientRect().top + pageY - gap;
    // A tall bundle block can push the add-to-cart button below the fold on desktop.
    // In that case scroll a bit further so the button is always in view.
    const cta = ppFirstVisible(PP_CTA);
    if (cta) {
      const ctaBottom = cta.getBoundingClientRect().bottom + pageY;
      const needed = ctaBottom + 24 - window.innerHeight;
      if (needed > top) top = needed;
    }
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    el.classList.add('pp-jump-flash');
    setTimeout(() => el.classList.remove('pp-jump-flash'), 1400);
  }

  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    if (!a || a.hasAttribute('data-pp-scroll')) return;
    const href = a.getAttribute('href') || '';
    const hash = href.indexOf('#');
    if (hash < 0) return;
    const path = href.slice(0, hash);
    if (path && path !== window.location.pathname) return; // link to another page: leave it alone
    const sel = PP_JUMPS[href.slice(hash + 1).toLowerCase()];
    if (!sel) return;
    e.preventDefault();
    const target = ppFirstVisible(sel);
    if (target) ppScrollTo(target, 90);
  });

  const init = (scope) => {
    scope.querySelectorAll('[data-pp-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-pp-countdown]').forEach(initCountdown);
    scope.querySelectorAll('[data-pp-expiry]').forEach(initExpiry);
    scope.querySelectorAll('[data-pp-timeline]').forEach(initTimeline);
    scope.querySelectorAll('[data-pp-mini-reviews]').forEach(initMiniReviews);
    scope.querySelectorAll('[data-pp-bundles]').forEach(initBundles);
    scope.querySelectorAll('[data-pp-scroll]').forEach(initScrollLink);
  };

  window.__ppFunnel = { init };
  const run = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => init(e.target.closest('.shopify-section') || document));
})();
